import type { ChatMessage, ChatRoomLock, EMessageType } from '@if/core/types'
import { getApiClient } from '../api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { chatMessageKeysFactory as unreadEntriesKeysFactory, type UnreadEntry } from '../chat-message/chat-message-api'

const CHAT_MESSAGES_URL = '/ChatMessages'
const LOCKS_URL = '/Chat/chatrooms'

export interface ConversationResponse {
  conversationNumber: string
  fullName: string
  email: string
  phone: string
  province: string
  merchantName: string
  merchantId: string
  merchantLanguage: number
  applicationCreatedByName: string
  systemMessage: string
  applicationStatus: string
  messages: ChatMessage[]
}

const messageKeysFactory = {
  all: () => [{ scope: 'chat-messages' }] as const,
  conversation: (creditApplicationId: string, financingProgramId: string) =>
    [{ scope: 'chat-messages', creditApplicationId, financingProgramId }] as const,
}

async function getConversation(creditApplicationId: string, financingProgramId: string): Promise<ConversationResponse> {
  const apiClient = getApiClient()
  const response = await apiClient.get<ConversationResponse>(
    `${CHAT_MESSAGES_URL}/${creditApplicationId}/${financingProgramId}`,
  )
  return response.data
}

export function useGetConversation(
  creditApplicationId: string | undefined,
  financingProgramId: string | undefined,
): [ConversationResponse | undefined, boolean, Error | null] {
  const { data, isLoading, error } = useQuery({
    queryKey: messageKeysFactory.conversation(creditApplicationId!, financingProgramId!),
    queryFn: () => getConversation(creditApplicationId!, financingProgramId!),
    enabled: !!creditApplicationId && !!financingProgramId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  return [data, isLoading, error]
}

export function useSendMessage() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async (message: {
      id: string
      content: string
      sentAt: Date
      sentByName: string
      sentById: string
      merchantId: string
      creditApplicationId: string
      financingProgramId: string
      isRead: boolean
      type: EMessageType
    }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<ChatMessage>(`${CHAT_MESSAGES_URL}/${message.creditApplicationId}`, message)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messageKeysFactory.all() })
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useEditMessage() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({
      creditApplicationId,
      messageId,
      content,
    }: {
      creditApplicationId: string
      messageId: string
      content: string
    }) => {
      const apiClient = getApiClient()
      await apiClient.put(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}`, { newContent: content })
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useDeleteMessage() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.delete(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkMessageRead() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.post(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}/MarkAsRead`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkMessageUnread() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.post(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}/MarkAsUnread`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkConversationAsRead() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId }: { creditApplicationId: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<{ success: boolean; messageCount: number }>(
        `${CHAT_MESSAGES_URL}/${creditApplicationId}/MarkAsRead`,
      )
      return response.data
    },
    onSuccess: (response, { creditApplicationId }) => {
      if (response.messageCount === 0) return
      queryClient.setQueryData<UnreadEntry[]>(unreadEntriesKeysFactory.unreadEntries(), (prev) =>
        prev ? prev.filter((e) => e.creditApplicationId !== creditApplicationId) : prev,
      )
    },
  })
  return [mutateAsync, isPending, error] as const
}

export { messageKeysFactory as chatMessageKeysFactory }

const lockKeysFactory = {
  all: () => [{ scope: 'chatroom-lock' }] as const,
  detail: (creditApplicationId: string) => [{ scope: 'chatroom-lock', creditApplicationId }] as const,
}

export function useGetChatRoomLock(
  creditApplicationId: string | undefined,
): [ChatRoomLock | undefined, boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: lockKeysFactory.detail(creditApplicationId!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    enabled: !!creditApplicationId,
  })
  return [data, isFetching, error]
}

export function useAcquireChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId }: { creditApplicationId: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useReleaseChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async (creditApplicationId: string) => {
      const apiClient = getApiClient()
      const response = await apiClient.delete<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useForceAcquireChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId }: { creditApplicationId: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.put<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export { lockKeysFactory as chatLockKeysFactory }
